import axios from 'axios';
import { createAsyncThunk, current, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IDevice, defaultValue } from 'app/shared/model/device.model';

const initialState: EntityState<IDevice> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  tableStats: [],
  importing: false,
  importResult: null,
};

const apiUrl = 'api/devices';

// Actions

export const getEntities = createAsyncThunk('device/fetch_entity_list', async ({ page, size, sort, filterUrl }: IQueryParams) => {
  const requestUrl = `${apiUrl}?${filterUrl ?? ''}&cacheBuster=${new Date().getTime()}`;
  return axios.get<IDevice[]>(requestUrl);
});

export const getEntitiesView = createAsyncThunk('device/fetch_entity_list', async ({ page, size, sort, filterUrl }: IQueryParams) => {
  const requestUrl = `${apiUrl}/views?${filterUrl ?? ''}&cacheBuster=${new Date().getTime()}`;
  return axios.get<IDevice[]>(requestUrl);
});

export const getDevicesByMaster = createAsyncThunk(
  'device/fetch_entity_list_by_master',
  async ({ page, size, sort, filterUrl }: IQueryParams) => {
    const requestUrl = `${apiUrl}?${filterUrl ?? ''}${
      sort ? `page=${page}&size=${size}&sort=${sort}&` : ''
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IDevice[]>(requestUrl);
  },
);
export const getSummaryCard = createAsyncThunk('device/fetch_summary_card', async () => {
  const requestUrl = `${apiUrl}/state/count`;
  return axios.get<{ count: number; status: string }[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'device/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IDevice>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'device/create_entity',
  async (entity: IDevice, thunkAPI) => {
    const result = await axios.post<IDevice>(apiUrl, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const linkEntity = createAsyncThunk(
  'device/link_entity',
  async (payload: any, thunkAPI) => {
    const result = await axios.put<IDevice>(apiUrl + `/link?serialNumber=${payload?.serialNumber}`, payload);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'device/update_entity',
  async (entity: IDevice, thunkAPI) => {
    const result = await axios.put<IDevice>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'device/partial_update_entity',
  async (entity: IDevice, thunkAPI) => {
    const result = await axios.patch<IDevice>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const importDevices = createAsyncThunk(
  'device/import_entities',
  async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post<IDevice>(`${apiUrl}/import`, formData);
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'device/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IDevice>(requestUrl);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const InventorySlice = createEntitySlice({
  name: 'inventory',
  initialState,
  reducers: {
    resetImportResult(state) {
      state.importResult = null;
      state.importing = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(importDevices.pending, state => {
        state.importing = true;
        state.importResult = null;
      })
      .addCase(importDevices.fulfilled, (state, action) => {
        state.importing = false;
        state.importResult = action.payload.data as any;
      })
      .addCase(importDevices.rejected, state => {
        state.importing = false;
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesView), (state, action) => {
        const { data, headers } = action.payload;
        return {
          ...state,
          loading: false,
          entities: data?.map(d => {
            return {
              ...d,
              subRows: [],
            };
          }),
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getDevicesByMaster), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: current(state).entities.map(entity => {
            return {
              ...entity,
              subRows: entity?.id === data?.[0]?.manager?.id ? data : entity?.subRows || [],
            };
          }),
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, linkEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getSummaryCard), (state, action) => {
        state.tableStats = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset, resetImportResult } = InventorySlice.actions;

// Reducer
export default InventorySlice.reducer;
